import React, { useState } from 'react';
import { Alert, Toast, ToastContainer } from 'react-bootstrap';
import { NotificationProps } from '../../types/common';
import { CartNotification } from './CartNotification';
import { getNotificationDuration } from '../../utils/cartHelpers';

interface NotificationState extends NotificationProps {
  id: string;
  show: boolean;
}

interface NotificationContextType {
  showNotification: (notification: Omit<NotificationProps, 'id'>) => void;
  hideNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = React.createContext<NotificationContextType | undefined>(undefined);

/**
 * Notification Provider component
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationState[]>([]);

  const showNotification = (notification: Omit<NotificationProps, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification: NotificationState = {
      ...notification,
      id,
      show: true,
    };

    setNotifications(prev => [...prev, newNotification]);

    // Use custom duration or default based on type
    const duration = getNotificationDuration(notification.type, notification.duration);

    // Auto-hide notification after duration
    if (duration !== 0) {
      setTimeout(() => {
        hideNotification(id);
      }, duration);
    }
  };

  const hideNotification = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, show: false }
          : notification
      )
    );

    // Remove notification from state after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 300);
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification, clearAllNotifications }}>
      {children}
      <style>
        {`
          .notification-toast .toast-header {
            border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
          }
          .notification-toast .toast-body {
            font-weight: 500;
            line-height: 1.4;
          }
          .notification-toast .btn-close {
            filter: invert(1) !important;
            opacity: 1 !important;
          }
          .notification-toast .btn-close:hover {
            opacity: 0.8 !important;
          }
        `}
      </style>
      <ToastContainer 
        position="top-end" 
        className="p-3"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          maxWidth: '400px'
        }}
      >
        {notifications.map(notification => {
          // Handle cart notifications specially
          if (notification.type === 'cart' && notification.cartData) {
            return (
              <Toast
                key={notification.id}
                show={notification.show}
                onClose={() => hideNotification(notification.id)}
                delay={getNotificationDuration('cart', notification.duration)}
                autohide={getNotificationDuration('cart', notification.duration) !== 0}
                className="cart-notification-toast"
                style={{
                  minWidth: '350px',
                  maxWidth: '400px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  border: 'none',
                  padding: 0
                }}
              >
                <CartNotification
                  {...notification.cartData}
                  onViewCart={notification.onViewCart || (() => {})}
                  onCheckout={notification.onCheckout || (() => {})}
                  onClose={() => hideNotification(notification.id)}
                  userId={notification.userId}
                  supermarketId={notification.supermarketId}
                  onRefresh={(freshData) => {
                    // Update the notification with fresh data
                    setNotifications(prev => prev.map(notif => 
                      notif.id === notification.id 
                        ? { ...notif, cartData: freshData }
                        : notif
                    ));
                  }}
                />
              </Toast>
            );
          }

          // Handle standard notifications
          return (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={getNotificationDuration(notification.type, notification.duration)}
              autohide={getNotificationDuration(notification.type, notification.duration) !== 0}
              bg={notification.type}
              className="notification-toast"
              style={{
                minWidth: '300px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: 'none',
                color: '#ffffff'
              }}
            >
              <Toast.Header 
                closeButton={true}
                style={{
                  backgroundColor: notification.type === 'success' ? '#198754' : 
                                 notification.type === 'error' ? '#dc3545' :
                                 notification.type === 'warning' ? '#ffc107' : '#0dcaf0',
                  color: '#ffffff',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <strong className="me-auto" style={{ color: notification.type === 'warning' ? '#000000' : '#ffffff' }}>
                  {notification.type === 'success' && '✓ Success'}
                  {notification.type === 'error' && '✗ Error'}
                  {notification.type === 'warning' && '⚠ Warning'}
                  {notification.type === 'info' && 'ℹ Info'}
                </strong>
              </Toast.Header>
              <Toast.Body 
                style={{
                  backgroundColor: notification.type === 'success' ? '#198754' : 
                                 notification.type === 'error' ? '#dc3545' :
                                 notification.type === 'warning' ? '#ffc107' : '#0dcaf0',
                  color: notification.type === 'warning' ? '#000000' : '#ffffff'
                }}
              >
                <div>
                  <strong style={{ color: notification.type === 'warning' ? '#000000' : '#ffffff' }}>
                    {notification.title}
                  </strong>
                  <div 
                    className="mt-1" 
                    style={{ color: notification.type === 'warning' ? '#000000' : '#ffffff' }}
                  >
                    {notification.message}
                  </div>
                </div>
              </Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>
    </NotificationContext.Provider>
  );
};

/**
 * Hook for using notifications
 */
export const useNotification = () => {
  const context = React.useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Simple notification component for inline use
 */
export const Notification: React.FC<NotificationProps & { onClose?: () => void }> = ({
  type,
  title,
  message,
  onClose,
}) => {
  return (
    <Alert variant={type} dismissible onClose={onClose}>
      <Alert.Heading>
        {type === 'success' && '✓ Success'}
        {type === 'error' && '✗ Error'}
        {type === 'warning' && '⚠ Warning'}
        {type === 'info' && 'ℹ Info'}
      </Alert.Heading>
      <div>
        <strong>{title}</strong>
        <div className="mt-1">{message}</div>
      </div>
    </Alert>
  );
};
